<template>
  <PmwFormField
    :label="label"
    :label-for="id"
    :error="error || hasValidationError"
    :required="required"
    :class="{ disabled: disabled }"
  >
    <div
      :class="{
        'cnpj-input__error': error || hasValidationError,
        'cnpj-input__disabled': disabled
      }"
      class="cnpj-input"
    >
      <the-mask
        :id="id"
        :value="value"
        :masked="false"
        :mask="mask"
        :tokens="tokens"
        :disabled="disabled"
        :placeholder="placeholder"
        type="text"
        class="cnpj-input--field"
        @input="handleInput"
        @blur="handleBlur"
      />
      <span v-if="hasValidationError" class="cnpj-input--error-message">
        {{ errorMessage }}
      </span>
    </div>
  </PmwFormField>
</template>

<script>
import { TheMask } from 'vue-the-mask'
import PmwFormField from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormField'
import { isValidCnpj, isValidCnpjFormat } from '@/helpers/cnpj'

/**
 * Campo de CNPJ alfanumérico com validação.
 *
 * Aceita o novo formato da Receita Federal (letras + números, a partir de jul/2026).
 * Aplica máscara XX.XXX.XXX/XXXX-XX e converte letras para maiúsculas em tempo real.
 * Emite o valor sem máscara (14 caracteres alfanuméricos em uppercase).
 *
 * @see https://www.gov.br/receitafederal/pt-br/acesso-a-informacao/acoes-e-programas/programas-e-atividades/cnpj-alfanumerico
 */
export default {
  name: 'CnpjInput',

  components: {
    TheMask,
    PmwFormField
  },

  props: {
    /** Valor do campo (sem máscara, 14 caracteres alfanuméricos) */
    value: {
      type: String,
      default: ''
    },
    /** Se o campo está com erro de validação (controlado externamente) */
    error: {
      type: Boolean,
      default: false
    },
    /** Label exibida acima do campo */
    label: {
      type: String,
      default: 'CNPJ'
    },
    /** ID do campo */
    id: {
      type: String,
      default: 'cnpj-input'
    },
    /** Placeholder do campo */
    placeholder: {
      type: String,
      default: '00.000.000/0000-00'
    },
    /** Se o campo é obrigatório */
    required: {
      type: Boolean,
      default: false
    },
    /** Se o campo está desabilitado */
    disabled: {
      type: Boolean,
      default: false
    },
    /** Se deve validar o CNPJ (dígitos verificadores) */
    validateCnpj: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      mask: 'XX.XXX.XXX/XXXX-XX',
      tokens: {
        X: {
          pattern: /[0-9a-zA-Z]/,
          transform: (v) => v.toUpperCase()
        }
      },
      touched: false,
      internalError: null
    }
  },

  computed: {
    /** Retorna se há erro de validação interno */
    hasValidationError() {
      return this.touched && this.internalError !== null
    },

    /** Mensagem de erro a ser exibida */
    errorMessage() {
      return this.internalError
    },

    /** Retorna se o CNPJ é válido */
    isValid() {
      if (!this.value && !this.required) return true
      if (!this.value && this.required) return false
      if (!this.validateCnpj) return isValidCnpjFormat(this.value)
      return isValidCnpj(this.value)
    }
  },

  methods: {
    /** Valida o CNPJ e atualiza estado de erro */
    validate() {
      this.touched = true

      if (!this.value && this.required) {
        this.internalError = 'CNPJ é obrigatório'
        this.$emit('validation', { valid: false, error: this.internalError })
        return false
      }

      if (this.value && this.value.length > 0 && this.value.length !== 14) {
        this.internalError = 'CNPJ deve ter 14 caracteres'
        this.$emit('validation', { valid: false, error: this.internalError })
        return false
      }

      if (this.value && this.validateCnpj && !isValidCnpj(this.value)) {
        this.internalError = 'CNPJ inválido'
        this.$emit('validation', { valid: false, error: this.internalError })
        return false
      }

      this.internalError = null
      this.$emit('validation', { valid: true, error: null })
      return true
    },

    /** Handler do evento input */
    handleInput(value) {
      this.$emit('input', value)

      /** Se já foi tocado, revalida em tempo real */
      if (this.touched) {
        this.$nextTick(() => this.validate())
      }
    },

    /** Handler do evento blur */
    handleBlur() {
      this.validate()
    },

    /** Reseta o estado de validação */
    reset() {
      this.touched = false
      this.internalError = null
    }
  }
}
</script>

<style lang="scss" scoped>
.cnpj-input {
  width: 100%;

  &--field {
    display: block;
    width: 100%;
    padding: 8px 0;
    border: none;
    border-bottom: 1px solid $gray-darkest;
    font-size: font-size('size-medium');
    background: transparent;

    &:focus {
      outline: none;
      border-color: $blue-2;
      border-bottom-width: 2px;
    }

    &::placeholder {
      color: $gray-mid;
    }
  }

  &--error-message {
    display: block;
    margin-top: 4px;
    font-size: font-size('size-small');
    color: $error;
  }

  &__error {
    .cnpj-input--field {
      border-color: $error;
      border-bottom-width: 2px;
    }
  }

  &__disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
</style>
